import { useCallback, useRef, useState } from 'react'
import { useService } from '../../../hooks/use-service'
import { AiGenerationDirectiveService } from '../../../services'
import { INotificationFieldSuggestions } from '../../../interfaces/notification-field-suggestions'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import {
    DEFAULT_PUSHLY_AI_GENERATION_OPTIONS,
    PushlyAiGenerationFlow,
    PushlyAiGenerationOptions,
    withBrandVoiceAvailability,
} from '../elements/pushly-ai-generation/generation-options'
import { PushlyAiApi } from './use-pushly-ai'

interface IPushlyAiGenerationFlowDeps {
    domainId: number
    pushlyAi: Pick<PushlyAiApi, 'generateNotificationVariants'>
    /** The builder variant generation reads its landing URL and audience from. */
    getSource(): NotificationVariantModel
    onResults(results: INotificationFieldSuggestions[]): void
}

/**
 * The Pushly AI drawer's stages — setup, generating, review — and the choices carried between them.
 * The choices outlive the drawer, so the next generation starts from the last one's cards.
 */
export const usePushlyAiGenerationFlow = ({
    domainId,
    pushlyAi,
    getSource,
    onResults,
}: IPushlyAiGenerationFlowDeps) => {
    const directiveSvc = useService(AiGenerationDirectiveService)
    const [flow, setFlowState] = useState<PushlyAiGenerationFlow | null>(null)
    // Read by the async steps, which would otherwise see the flow as it was when they started.
    const flowRef = useRef<PushlyAiGenerationFlow | null>(null)
    const lastOptionsRef = useRef<PushlyAiGenerationOptions>(DEFAULT_PUSHLY_AI_GENERATION_OPTIONS)
    // Bumped whenever the drawer is closed or reopened, so work started before that is dropped
    // rather than landing in a drawer the reviewer walked away from.
    const runRef = useRef(0)

    const setFlow = useCallback((next: PushlyAiGenerationFlow | null) => {
        flowRef.current = next
        setFlowState(next)
    }, [])
    const patchFlow = useCallback(
        (patch: Partial<PushlyAiGenerationFlow>) => {
            if (flowRef.current) setFlow({ ...flowRef.current, ...patch })
        },
        [setFlow],
    )

    const open = useCallback(() => {
        const run = ++runRef.current
        setFlow({
            stage: 'setup',
            options: lastOptionsRef.current,
            brandVoiceLoading: true,
            regenerating: false,
            error: null,
            empty: false,
        })

        // Re-checked on every open: Brand Voice can be set up or cleared in another tab while the
        // builder stays mounted.
        directiveSvc
            .fetchBrandVoice(domainId, { showLoadingScreen: false })
            .then((res) => !!res.data?.active_version?.body)
            .catch(() => false)
            .then((found) => {
                if (runRef.current !== run || !flowRef.current) return
                patchFlow({
                    brandVoiceLoading: false,
                    options: withBrandVoiceAvailability(flowRef.current.options, found),
                })
            })
    }, [directiveSvc, domainId, setFlow, patchFlow])

    const changeOptions = useCallback(
        (options: PushlyAiGenerationOptions) => {
            lastOptionsRef.current = options
            patchFlow({ options })
        },
        [patchFlow],
    )

    // The drawer stays open while generating: the cards turn into skeletons, then into the results.
    const generate = useCallback(async () => {
        const current = flowRef.current
        if (!current || current.stage !== 'setup') return

        const run = runRef.current
        patchFlow({ stage: 'generating', error: null, empty: false })

        const { allSuggestions, error } = await pushlyAi
            .generateNotificationVariants(getSource(), current.options, current.regenerating)
            .catch((e: unknown) => {
                console.warn(e)
                return { allSuggestions: null, error: null }
            })

        if (runRef.current !== run) return

        // Errors and empty responses go back to setup, so the same cards can be retried as they are.
        if (error) return patchFlow({ stage: 'setup', error })
        if (!allSuggestions?.length) return patchFlow({ stage: 'setup', empty: true })

        onResults(allSuggestions)
        patchFlow({ stage: 'review', regenerating: false })
    }, [pushlyAi, getSource, onResults, patchFlow])

    const regenerate = useCallback(() => {
        patchFlow({ stage: 'setup', regenerating: true, error: null, empty: false })
    }, [patchFlow])

    // There is no way to abort the request itself, so closing only walks away from its result.
    const close = useCallback(() => {
        runRef.current++
        setFlow(null)
    }, [setFlow])

    return { flow, open, changeOptions, generate, regenerate, close }
}
